import type { TeamDto, WebPolicyDto } from '@dbrain/shared';
import { Alert, Badge, Button, Card, ConfirmDialog, EmptyState, Field, Select, TextArea, TextInput } from '@dbrain/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../api/client.js';
import { capKeys, useTeamMembers, useTeams, useWebPolicy } from '../../api/capabilities.js';
import { useMembers } from '../../api/hooks.js';
import { ErrorAlert } from '../ErrorAlert.js';

/**
 * Organization settings of Phase 11: teams (the team level of the skill
 * library, SA-047) and the web and integration policy — search provider,
 * page reading, allowed and blocked sites, external skill sources, the
 * users' own MCP servers (D-11, D-13, D-37; SA-039, SA-044).
 */
function TeamMembers({ team, admin }: { team: TeamDto; admin: boolean }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const members = useTeamMembers(team.id);
  const tenantMembers = useMembers();
  const [userId, setUserId] = useState('');
  const canManage = admin || team.myRole === 'lead';
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: capKeys.teamMembers(team.id) });
    void queryClient.invalidateQueries({ queryKey: capKeys.teams });
  };
  const put = useMutation({ mutationFn: (input: { userId: string; role: 'lead' | 'member' }) => api('PUT', `/api/teams/${team.id}/members/${input.userId}`, { role: input.role }), onSuccess: refresh });
  const remove = useMutation({ mutationFn: (id: string) => api('DELETE', `/api/teams/${team.id}/members/${id}`), onSuccess: refresh });
  const inTeam = new Set((members.data ?? []).map((m) => m.userId));
  return (
    <div className="team-members">
      <ul className="list">
        {(members.data ?? []).map((member) => (
          <li key={member.userId} className="list__item">
            <span>
              {member.displayName} <span className="muted">{member.email}</span>
            </span>
            <span className="row">
              {canManage ? (
                <Select value={member.role} onChange={(e) => put.mutate({ userId: member.userId, role: e.target.value as 'lead' | 'member' })} aria-label={t('teams.role')}>
                  <option value="lead">{t('teams.lead')}</option>
                  <option value="member">{t('teams.member')}</option>
                </Select>
              ) : (
                <Badge>{member.role === 'lead' ? t('teams.lead') : t('teams.member')}</Badge>
              )}
              {canManage ? (
                <Button size="sm" variant="ghost" onClick={() => remove.mutate(member.userId)}>
                  {t('teams.remove')}
                </Button>
              ) : null}
            </span>
          </li>
        ))}
      </ul>
      {canManage ? (
        <div className="row">
          <Select value={userId} onChange={(e) => setUserId(e.target.value)} aria-label={t('teams.addMember')} data-testid="team-add-member">
            <option value="">{t('teams.pickMember')}</option>
            {(tenantMembers.data ?? [])
              .filter((m) => !inTeam.has(m.userId))
              .map((m) => (
                <option key={m.userId} value={m.userId}>
                  {m.displayName}
                </option>
              ))}
          </Select>
          <Button size="sm" disabled={!userId} onClick={() => put.mutate({ userId, role: 'member' })}>
            {t('teams.addMember')}
          </Button>
        </div>
      ) : null}
      <ErrorAlert error={put.error ?? remove.error ?? members.error} />
    </div>
  );
}

export function TeamsCard({ admin }: { admin: boolean }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const teams = useTeams();
  const [name, setName] = useState('');
  const [open, setOpen] = useState<string | null>(null);
  const create = useMutation({
    mutationFn: () => api<TeamDto>('POST', '/api/teams', { name: name.trim() }),
    onSuccess: () => {
      setName('');
      void queryClient.invalidateQueries({ queryKey: capKeys.teams });
    },
  });
  const [removing, setRemoving] = useState<{ id: string; name: string } | null>(null);
  const remove = useMutation({
    mutationFn: (id: string) => api('DELETE', `/api/teams/${id}`),
    onSuccess: () => {
      setRemoving(null);
      void queryClient.invalidateQueries({ queryKey: capKeys.teams });
    },
  });
  return (
    <Card title={t('teams.title')} data-testid="teams-card">
      <p className="muted">{t('teams.intro')}</p>
      {teams.data?.length === 0 ? <EmptyState>{t('teams.empty')}</EmptyState> : null}
      <ul className="list">
        {(teams.data ?? []).map((team) => (
          <li key={team.id} className="list__item team-row">
            <div className="team-row__body">
              <button type="button" className="link-button" onClick={() => setOpen(open === team.id ? null : team.id)} data-testid="team-open">
                <strong>{team.name}</strong>
              </button>{' '}
              <span className="muted">{t('teams.count', { count: team.memberCount })}</span>
              {team.myRole ? <Badge tone="accent">{team.myRole === 'lead' ? t('teams.lead') : t('teams.member')}</Badge> : null}
              {open === team.id ? <TeamMembers team={team} admin={admin} /> : null}
            </div>
            {admin ? (
              <Button size="sm" variant="danger" onClick={() => setRemoving({ id: team.id, name: team.name })} data-testid="team-delete">
                {t('common.delete')}
              </Button>
            ) : null}
          </li>
        ))}
      </ul>
      {removing ? (
        <ConfirmDialog
          title={t('teams.deleteTitle', { name: removing.name })}
          confirmLabel={t('common.delete')}
          cancelLabel={t('common.cancel')}
          closeLabel={t('common.close')}
          busy={remove.isPending}
          error={<ErrorAlert error={remove.error} />}
          onConfirm={() => remove.mutate(removing.id)}
          onClose={() => setRemoving(null)}
        >
          {t('teams.confirmDelete', { name: removing.name })}
        </ConfirmDialog>
      ) : null}
      {admin ? (
        <form
          className="row"
          onSubmit={(e) => {
            e.preventDefault();
            if (name.trim()) create.mutate();
          }}
        >
          <TextInput value={name} onChange={(e) => setName(e.target.value)} placeholder={t('teams.newPlaceholder')} aria-label={t('teams.new')} data-testid="team-name" />
          <Button type="submit" size="sm" busy={create.isPending} disabled={!name.trim()} data-testid="team-create">
            {t('teams.new')}
          </Button>
        </form>
      ) : null}
      <ErrorAlert error={create.error ?? remove.error ?? teams.error} />
    </Card>
  );
}

const lines = (text: string) => text.split('\n').map((s) => s.trim()).filter(Boolean);

export function WebPolicyCard() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const policy = useWebPolicy();
  const [draft, setDraft] = useState<Partial<WebPolicyDto> & { allowText?: string; denyText?: string }>({});
  const [key, setKey] = useState('');
  const current = { ...policy.data, ...draft } as WebPolicyDto & { allowText?: string; denyText?: string };
  const save = useMutation({
    mutationFn: () =>
      api<WebPolicyDto>('PATCH', '/api/tenant/web-policy', {
        ...(draft.webSearchProvider !== undefined ? { webSearchProvider: draft.webSearchProvider } : {}),
        ...(draft.webSearchUrl !== undefined ? { webSearchUrl: draft.webSearchUrl || null } : {}),
        ...(draft.webFetchEnabled !== undefined ? { webFetchEnabled: draft.webFetchEnabled } : {}),
        ...(draft.allowText !== undefined ? { allowDomains: lines(draft.allowText) } : {}),
        ...(draft.denyText !== undefined ? { denyDomains: lines(draft.denyText) } : {}),
        ...(draft.skillExternalSources !== undefined ? { skillExternalSources: draft.skillExternalSources } : {}),
        ...(draft.mcpUserServers !== undefined ? { mcpUserServers: draft.mcpUserServers } : {}),
      }),
    onSuccess: (data) => {
      queryClient.setQueryData(capKeys.webPolicy, data);
      setDraft({});
    },
  });
  const saveKey = useMutation({
    mutationFn: (apiKey: string | null) => api<WebPolicyDto>('PUT', '/api/tenant/web-policy/search-key', { apiKey }),
    onSuccess: (data) => {
      queryClient.setQueryData(capKeys.webPolicy, data);
      setKey('');
    },
  });
  if (!policy.data) return <ErrorAlert error={policy.error} />;
  const provider = current.webSearchProvider;
  const paid = provider === 'brave' || provider === 'tavily';
  return (
    <Card title={t('webPolicy.title')} data-testid="web-policy-card">
      <p className="muted">{t('webPolicy.intro')}</p>
      <Field label={t('webPolicy.provider')} hint={provider === 'platform' && !policy.data.platformSearch ? t('webPolicy.platformMissing') : undefined}>
        {({ id }) => (
          <Select id={id} value={provider} onChange={(e) => setDraft((d) => ({ ...d, webSearchProvider: e.target.value as WebPolicyDto['webSearchProvider'] }))} data-testid="web-search-provider">
            <option value="platform">{t('webPolicy.providers.platform')}</option>
            <option value="searxng">{t('webPolicy.providers.searxng')}</option>
            <option value="brave">Brave Search</option>
            <option value="tavily">Tavily</option>
            <option value="off">{t('webPolicy.providers.off')}</option>
          </Select>
        )}
      </Field>
      {provider === 'searxng' ? (
        <Field label={t('webPolicy.searxngUrl')}>
          {({ id }) => <TextInput id={id} value={current.webSearchUrl ?? ''} onChange={(e) => setDraft((d) => ({ ...d, webSearchUrl: e.target.value }))} placeholder="https://search.example.com" />}
        </Field>
      ) : null}
      {paid ? (
        <div className="row">
          <Field label={t('webPolicy.apiKey')} hint={policy.data.hasSearchKey ? t('webPolicy.keySaved') : t('webPolicy.keyMissing')}>
            {({ id }) => <TextInput id={id} type="password" autoComplete="off" value={key} onChange={(e) => setKey(e.target.value)} data-testid="web-search-key" />}
          </Field>
          <Button size="sm" disabled={key.trim().length < 8} busy={saveKey.isPending} onClick={() => saveKey.mutate(key.trim())}>
            {t('webPolicy.saveKey')}
          </Button>
          {policy.data.hasSearchKey ? (
            <Button size="sm" variant="ghost" onClick={() => saveKey.mutate(null)}>
              {t('webPolicy.removeKey')}
            </Button>
          ) : null}
        </div>
      ) : null}
      <label className="switch-row">
        <input type="checkbox" checked={current.webFetchEnabled} onChange={(e) => setDraft((d) => ({ ...d, webFetchEnabled: e.target.checked }))} data-testid="web-fetch-enabled" />
        <span>
          <strong>{t('webPolicy.fetch')}</strong> <span className="muted">— {t('webPolicy.fetchHint')}</span>
        </span>
      </label>
      <div className="row">
        <Field label={t('webPolicy.allow')} hint={t('webPolicy.allowHint')}>
          {({ id }) => <TextArea id={id} rows={3} value={current.allowText ?? policy.data.allowDomains.join('\n')} onChange={(e) => setDraft((d) => ({ ...d, allowText: e.target.value }))} />}
        </Field>
        <Field label={t('webPolicy.deny')} hint={t('webPolicy.denyHint')}>
          {({ id }) => <TextArea id={id} rows={3} value={current.denyText ?? policy.data.denyDomains.join('\n')} onChange={(e) => setDraft((d) => ({ ...d, denyText: e.target.value }))} data-testid="web-deny" />}
        </Field>
      </div>
      <label className="switch-row">
        <input type="checkbox" checked={current.skillExternalSources} onChange={(e) => setDraft((d) => ({ ...d, skillExternalSources: e.target.checked }))} data-testid="skill-external" />
        <span>
          <strong>{t('webPolicy.skillSources')}</strong> <span className="muted">— {t('webPolicy.skillSourcesHint')}</span>
        </span>
      </label>
      <label className="switch-row">
        <input type="checkbox" checked={current.mcpUserServers} onChange={(e) => setDraft((d) => ({ ...d, mcpUserServers: e.target.checked }))} data-testid="mcp-user-servers" />
        <span>
          <strong>{t('webPolicy.userServers')}</strong> <span className="muted">— {t('webPolicy.userServersHint')}</span>
        </span>
      </label>
      {Object.keys(draft).length > 0 ? (
        <div className="row">
          <Button busy={save.isPending} onClick={() => save.mutate()} data-testid="web-policy-save">
            {t('common.save')}
          </Button>
          <Button variant="ghost" onClick={() => setDraft({})}>
            {t('common.cancel')}
          </Button>
        </div>
      ) : null}
      {save.isSuccess && Object.keys(draft).length === 0 ? <Alert tone="success" title={t('common.saved')} /> : null}
      <ErrorAlert error={save.error ?? saveKey.error} />
    </Card>
  );
}
